import { useState, useEffect } from 'react';
import { X, AlertCircle, Loader } from 'lucide-react';
import { incidentsApiService } from '../services/incidentsApi';
import { Incident, IncidentStatus } from '../types/incident';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownEditor from './MarkdownEditor';

interface EditIncidentModalProps {
  incident: Incident;
  onClose: () => void;
  onSave: (updatedIncident: Incident) => void;
  isLoading?: boolean;
  /**
   * When true, route the save through `PATCH /incidents/{id}/with-sync` so that
   * status / priority changes propagate to bridged kanban cards (per per-link flags).
   * Defaults to `false` (legacy endpoint, identical body, zero side effects).
   *
   * Parents should set this from `shouldUseSyncForIncident(kanbanLinks)`.
   */
  useSync?: boolean;
}

const STATUS_OPTIONS: IncidentStatus[] = ['active', 'critical', 'resolved', 'closed', 'pending', 'investigating'];
const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const EditIncidentModal = ({
  incident,
  onClose,
  onSave,
  isLoading = false,
  useSync = false,
}: EditIncidentModalProps) => {
  const [title, setTitle] = useState(incident.title);
  const [description, setDescription] = useState(incident.description);
  const [status, setStatus] = useState<IncidentStatus>(incident.status);
  const [priority, setPriority] = useState(incident.priority);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(incident.title);
    setDescription(incident.description);
    setStatus(incident.status);
    setPriority(incident.priority);
  }, [incident]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const body = { title, description, status, priority };
      const updatedIncident = useSync
        ? await incidentsApiService.updateIncidentWithSync(incident.id, body)
        : await incidentsApiService.updateIncident(incident.id, body);
      onSave(updatedIncident);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update incident');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-panel max-w-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 className="modal-title">Edit Incident</h2>
            <button onClick={onClose} className="modal-close" title="Close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="glass-input w-full"
                placeholder="Brief incident title..."
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Description <span className="text-red-400">*</span>
              </label>
              <MarkdownEditor
                value={description}
                onChange={setDescription}
                height={300}
                placeholder="Enter incident description in markdown..."
              />
              <p className="text-xs text-glass-500 mt-1">
                Markdown editor with live preview - use code blocks, tables, lists, etc.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as IncidentStatus)}
                  className="glass-input w-full"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Priority (1 = highest, 10 = lowest)
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="glass-input w-full"
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>Priority {p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="glass-button-secondary"
                disabled={isSubmitting || isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting || isLoading || !title || !description}
              >
                {(isSubmitting || isLoading) ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

